import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "closeDelay", "delay", "handle", "nativeButton", "openOnHover", "payload", "render", "style", "xstyle"],
  _excluded2 = ["anchor", "children", "className", "side", "align", "sideOffset", "style", "xstyle"],
  _excluded3 = ["children", "className", "style", "xstyle"],
  _excluded4 = ["children", "className", "style", "xstyle"],
  _excluded5 = ["children", "className", "render", "nativeButton", "style", "xstyle"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef } from 'react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import * as stylex from '@stylexjs/stylex';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { focusRingInset, focusRingOutset } from '../../styles/focus-ring';
import { zIndexVars } from '../../constants/zIndex.stylex';
import { assertElementRender } from '../../utils/assertElementRender';
import { LayoutConstraintsReset } from '../layout/LayoutConstraints';
import { warnMutuallyExclusive } from '../../utils/warnMutuallyExclusive';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_COMPONENT_NAME = 'Popover';
const POPOVER_TRIGGER_COMPONENT_NAME = 'PopoverTrigger';
const POPOVER_CONTENT_COMPONENT_NAME = 'PopoverContent';
const POPOVER_TITLE_COMPONENT_NAME = 'PopoverTitle';
const POPOVER_DESCRIPTION_COMPONENT_NAME = 'PopoverDescription';
const POPOVER_CLOSE_COMPONENT_NAME = 'PopoverClose';
export const createPopoverHandle = BasePopover.createHandle;
const fadeIn = "x18re5ia-B";
const styles = {
  trigger: {
    backgroundColor: "x1gjadoe",
    borderStyle: "xng3xce",
    margin: "x1ghz6dp",
    padding: "x1717udv",
    $$css: true
  },
  popup: {
    backgroundColor: "xizas6a",
    borderColor: "x18stzcf",
    borderRadius: "x12ruo1t",
    borderStyle: "x10trpm",
    borderWidth: "xmhl9si",
    boxShadow: "x1yp7lcc",
    color: "xnp8pqq",
    display: "x78zum5",
    flexDirection: "xdt5ytf",
    fontSize: "xe639uq",
    gap: "x9ifctm",
    outline: "x1a2a7pz",
    paddingBlock: "x1xmcnkz",
    paddingInline: "x1s05lik",
    transform: "x3oybdh xy2yp29 x1564mui",
    transformOrigin: "xctpfpq",
    transitionDuration: "xx6bhzk x12w9bfk",
    transitionProperty: "x11xpdln",
    transitionTimingFunction: "x9lcvmn",
    $$css: true
  },
  title: {
    fontSize: "x1hbkd3u",
    fontWeight: "x1ogaumu",
    lineHeight: "x1wdnn3i",
    marginBlock: "x1gdcfkm",
    $$css: true
  },
  description: {
    color: "xnp8pqq",
    fontSize: "xe639uq",
    marginBlock: "x1gdcfkm",
    $$css: true
  },
  close: {
    backgroundColor: "x1gjadoe",
    borderColor: "x18stzcf",
    borderRadius: "x1nq0khy",
    borderStyle: "x10trpm",
    borderWidth: "xmhl9si",
    cursor: "x1ypdohk",
    outline: "x1a2a7pz",
    paddingBlock: "x1n5c368",
    paddingInline: "x1t0a19f",
    $$css: true
  }
};

/**
 * Displays contextual content in a floating panel anchored to a trigger. Use for inline help, filter panels, and quick-edit forms; for blocking dialogs, use Dialog instead.
 *
 * This is the primitive version. Prefer the config API, which covers most use cases.
 *
 * @see {@link __PopoverConfig | Popover}
 */
export function Popover(t0) {
  const $ = _c(6);
  const {
    children,
    defaultOpen,
    handle,
    onOpenChange,
    open
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== defaultOpen || $[2] !== handle || $[3] !== onOpenChange || $[4] !== open) {
    t1 = /*#__PURE__*/_jsx(BasePopover.Root, {
      defaultOpen: defaultOpen,
      handle: handle,
      onOpenChange: onOpenChange,
      open: open,
      children: children
    });
    $[0] = children;
    $[1] = defaultOpen;
    $[2] = handle;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
Popover.displayName = POPOVER_COMPONENT_NAME;

/**
 * Button that toggles the popover. Pass non-interactive content (text, icons) as `children` — the trigger wraps them in a focusable `<button>`. Use the `render` prop to replace the default button with an existing interactive element like `Button`. For detached triggers, pass a shared `handle` created with `createPopoverHandle()`. For info icons that reveal explanatory text, set `openOnHover` to make an infotip instead of using a Tooltip.
 */
export const PopoverTrigger = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(27);
  let children;
  let closeDelay;
  let delay;
  let handle;
  let nativeButton;
  let openOnHover;
  let payload;
  let render;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        closeDelay: t2,
        delay: t3,
        handle: t4,
        nativeButton: t5,
        openOnHover: t6,
        payload: t7,
        render: t8,
        xstyle: t9
      } = t0,
      t10 = _objectWithoutPropertiesLoose(t0, _excluded);
    children = t1;
    closeDelay = t2;
    delay = t3;
    handle = t4;
    nativeButton = t5;
    openOnHover = t6;
    payload = t7;
    render = t8;
    xstyle = t9;
    restProps = t10;
    $[0] = t0;
    $[1] = children;
    $[2] = closeDelay;
    $[3] = delay;
    $[4] = handle;
    $[5] = nativeButton;
    $[6] = openOnHover;
    $[7] = payload;
    $[8] = render;
    $[9] = restProps;
    $[10] = xstyle;
  } else {
    children = $[1];
    closeDelay = $[2];
    delay = $[3];
    handle = $[4];
    nativeButton = $[5];
    openOnHover = $[6];
    payload = $[7];
    render = $[8];
    restProps = $[9];
    xstyle = $[10];
  }
  assertElementRender("PopoverTrigger", render);
  warnMutuallyExclusive("PopoverTrigger", render, xstyle);
  let t1;
  if ($[11] !== render || $[12] !== xstyle) {
    t1 = stylex.props(render == null && styles.trigger, render == null && focusRingOutset.default, render == null ? xstyle : undefined);
    $[11] = render;
    $[12] = xstyle;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: POPOVER_TRIGGER_COMPONENT_NAME
    };
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== children || $[16] !== closeDelay || $[17] !== delay || $[18] !== handle || $[19] !== nativeButton || $[20] !== openOnHover || $[21] !== payload || $[22] !== ref || $[23] !== render || $[24] !== restProps || $[25] !== t1) {
    t3 = /*#__PURE__*/_jsx(BasePopover.Trigger, Object.assign({
      closeDelay: closeDelay,
      delay: delay,
      handle: handle,
      nativeButton: nativeButton,
      openOnHover: openOnHover,
      payload: payload,
      ref: ref,
      render: render
    }, restProps, t1, t2, {
      children: children
    }));
    $[15] = children;
    $[16] = closeDelay;
    $[17] = delay;
    $[18] = handle;
    $[19] = nativeButton;
    $[20] = openOnHover;
    $[21] = payload;
    $[22] = ref;
    $[23] = render;
    $[24] = restProps;
    $[25] = t1;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  return t3;
});
PopoverTrigger.displayName = POPOVER_TRIGGER_COMPONENT_NAME;

/**
 * Popover content container. Renders inside a portal and positions itself relative
 * to the trigger. Configurable placement via `side`, `align`, and `sideOffset`.
 */
const _temp = {
  "x-default-marker": "x-default-marker",
  $$css: true
};
export const PopoverContent = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(26);
  let anchor;
  let children;
  let restProps;
  let t1;
  let t2;
  let t3;
  let xstyle;
  if ($[0] !== t0) {
    const {
        anchor: t4,
        children: t5,
        side: t6,
        align: t7,
        sideOffset: t8,
        xstyle: t9
      } = t0,
      t10 = _objectWithoutPropertiesLoose(t0, _excluded2);
    anchor = t4;
    children = t5;
    t1 = t6;
    t2 = t7;
    t3 = t8;
    xstyle = t9;
    restProps = t10;
    $[0] = t0;
    $[1] = anchor;
    $[2] = children;
    $[3] = restProps;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = xstyle;
  } else {
    anchor = $[1];
    children = $[2];
    restProps = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    xstyle = $[7];
  }
  const side = t1 === undefined ? "bottom" : t1;
  const align = t2 === undefined ? "center" : t2;
  const sideOffset = t3 === undefined ? 4 : t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      className: "x1hc1fzr x1axp6c6 xtsvl71 x1u6grsq xqcmdr3 xa3vuyk xx6bhzk x12w9bfk x19991ni x9lcvmn x1btqfyf"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== xstyle) {
    t5 = stylex.props(styles.popup, _temp, xstyle);
    $[9] = xstyle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [DATA_COMPONENT_ATTRIBUTE]: POPOVER_CONTENT_COMPONENT_NAME
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children) {
    t7 = /*#__PURE__*/_jsx(LayoutConstraintsReset, {
      children: children
    });
    $[12] = children;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== ref || $[15] !== restProps || $[16] !== t5 || $[17] !== t7) {
    t8 = /*#__PURE__*/_jsx(BasePopover.Popup, Object.assign({}, restProps, {
      ref: ref
    }, t5, t6, {
      children: t7
    }));
    $[14] = ref;
    $[15] = restProps;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = /*#__PURE__*/_jsx(BasePopover.Arrow, Object.assign({}, {
      className: "xizas6a xnv2psr x16stqrj x1mdjfac xuecr35 x1v8p93f x1bo5mbi xr8ek0q x1kgradm xx9nvtc xej8tak x1xanhnw x1o3jo1z x1jd1if x1fumljk xv5lvn5 xodq0cn xlc5lq9 xy9i3ba x4lcdl0 x1pvnxhb xpiw0pq x10trpm xmhl9si xyzo0hk xn7w30p x1rwnymp x1ngbm5e xi6f4yf xvihmje xox2cij x1tvzbhf xix3ylq x1qxjrq x15cp64r x13kwbcg x1alxdme x1e5vmcq xjm743q x1d9q6op xx6bhzk x12w9bfk x11xpdln x9lcvmn"
    }));
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== align || $[21] !== anchor || $[22] !== side || $[23] !== sideOffset || $[24] !== t8) {
    t10 = /*#__PURE__*/_jsx(BasePopover.Portal, {
      children: /*#__PURE__*/_jsxs(BasePopover.Positioner, Object.assign({
        anchor: anchor,
        side: side,
        align: align,
        sideOffset: sideOffset,
        arrowPadding: 16
      }, t4, {
        children: [t8, t9]
      }))
    });
    $[20] = align;
    $[21] = anchor;
    $[22] = side;
    $[23] = sideOffset;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
});
PopoverContent.displayName = POPOVER_CONTENT_COMPONENT_NAME;

/** Heading element for the popover, rendered as a styled title. */
export const PopoverTitle = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(12);
  let children;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        xstyle: t2
      } = t0,
      t3 = _objectWithoutPropertiesLoose(t0, _excluded3);
    children = t1;
    xstyle = t2;
    restProps = t3;
    $[0] = t0;
    $[1] = children;
    $[2] = restProps;
    $[3] = xstyle;
  } else {
    children = $[1];
    restProps = $[2];
    xstyle = $[3];
  }
  let t1;
  if ($[4] !== xstyle) {
    t1 = stylex.props(styles.title, xstyle);
    $[4] = xstyle;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: POPOVER_TITLE_COMPONENT_NAME
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== ref || $[9] !== restProps || $[10] !== t1) {
    t3 = /*#__PURE__*/_jsx(BasePopover.Title, Object.assign({}, restProps, {
      ref: ref
    }, t1, t2, {
      children: children
    }));
    $[7] = children;
    $[8] = ref;
    $[9] = restProps;
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
PopoverTitle.displayName = POPOVER_TITLE_COMPONENT_NAME;

/** Descriptive text element for the popover content. */
export const PopoverDescription = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(12);
  let children;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        xstyle: t2
      } = t0,
      t3 = _objectWithoutPropertiesLoose(t0, _excluded4);
    children = t1;
    xstyle = t2;
    restProps = t3;
    $[0] = t0;
    $[1] = children;
    $[2] = restProps;
    $[3] = xstyle;
  } else {
    children = $[1];
    restProps = $[2];
    xstyle = $[3];
  }
  let t1;
  if ($[4] !== xstyle) {
    t1 = stylex.props(styles.description, xstyle);
    $[4] = xstyle;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: POPOVER_DESCRIPTION_COMPONENT_NAME
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== ref || $[9] !== restProps || $[10] !== t1) {
    t3 = /*#__PURE__*/_jsx(BasePopover.Description, Object.assign({}, restProps, {
      ref: ref
    }, t1, t2, {
      children: children
    }));
    $[7] = children;
    $[8] = ref;
    $[9] = restProps;
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
PopoverDescription.displayName = POPOVER_DESCRIPTION_COMPONENT_NAME;

/**
 * Close button that dismisses the popover. Renders a styled button by default,
 * or a custom element via the `render` prop.
 */
export const PopoverClose = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(17);
  let children;
  let nativeButton;
  let render;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        render: t2,
        nativeButton: t3,
        xstyle: t4
      } = t0,
      t5 = _objectWithoutPropertiesLoose(t0, _excluded5);
    children = t1;
    render = t2;
    nativeButton = t3;
    xstyle = t4;
    restProps = t5;
    $[0] = t0;
    $[1] = children;
    $[2] = nativeButton;
    $[3] = render;
    $[4] = restProps;
    $[5] = xstyle;
  } else {
    children = $[1];
    nativeButton = $[2];
    render = $[3];
    restProps = $[4];
    xstyle = $[5];
  }
  assertElementRender("PopoverClose", render);
  warnMutuallyExclusive("PopoverClose", render, xstyle);
  let t1;
  if ($[6] !== render || $[7] !== xstyle) {
    t1 = render == null && stylex.props(styles.close, focusRingInset.default, xstyle);
    $[6] = render;
    $[7] = xstyle;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: POPOVER_CLOSE_COMPONENT_NAME
    };
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== nativeButton || $[12] !== ref || $[13] !== render || $[14] !== restProps || $[15] !== t1) {
    t3 = /*#__PURE__*/_jsx(BasePopover.Close, Object.assign({}, restProps, {
      ref: ref,
      render: render,
      nativeButton: nativeButton
    }, t1, t2, {
      children: children
    }));
    $[10] = children;
    $[11] = nativeButton;
    $[12] = ref;
    $[13] = render;
    $[14] = restProps;
    $[15] = t1;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
});
PopoverClose.displayName = POPOVER_CLOSE_COMPONENT_NAME;